import React, { useState, useEffect, useRef } from 'react';
import {
  Box, Typography, Stack, Paper, Button, TextField, MenuItem,
  Select, FormControl, InputLabel, IconButton, Chip, Tooltip,
  Divider, CircularProgress, LinearProgress,
} from '@mui/material';
import {
  CloudUpload as UploadIcon, Delete as DeleteIcon,
  Download as DownloadIcon, Description as FileIcon,
  PictureAsPdf as PdfIcon, Image as ImageIcon,
  InsertDriveFile as DocIcon, Refresh as RefreshIcon,
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import salesAPI from '../../api/sales';

const fmtDate = (d) => d
  ? new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' })
  : '—';

const fmtSize = (bytes) => {
  if (!bytes) return '—';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const DOC_TYPE_OPTIONS = [
  { value: 'business_bank_statement', label: 'Business Bank Statement' },
  { value: 'personal_bank_statement', label: 'Personal Bank Statement' },
  { value: 'business_tax_return', label: 'Business Tax Return' },
  { value: 'personal_tax_return', label: 'Personal Tax Return' },
  { value: 'profit_loss', label: 'Profit & Loss' },
  { value: 'balance_sheet', label: 'Balance Sheet' },
  { value: 'profit_loss_ytd', label: 'YTD Profit & Loss' },
  { value: 'balance_sheet_ytd', label: 'YTD Balance Sheet' },
  { value: 'drivers_license', label: "Driver's License" },
  { value: 'voided_check', label: 'Voided Check' },
  { value: 'lease_agreement', label: 'Lease Agreement' },
  { value: 'mortgage_statement', label: 'Mortgage Statement' },
  { value: 'business_license', label: 'Business License' },
  { value: 'articles_incorporation', label: 'Articles of Incorporation' },
  { value: 'schedule_k1', label: 'Schedule K-1' },
  { value: 'application_pdf', label: 'Application PDF' },
  { value: 'pfs_pdf', label: 'PFS PDF' },
  { value: 'other', label: 'Other' },
];

const DOC_TYPE_LABELS = Object.fromEntries(DOC_TYPE_OPTIONS.map(o => [o.value, o.label]));

const STATUS_COLORS = {
  pending: 'warning',
  under_review: 'info',
  approved: 'success',
  rejected: 'error',
  complete: 'success',
  signed: 'success',
  missing: 'default',
};

const FILE_ICONS = {
  'application/pdf': <PdfIcon fontSize="small" sx={{ color: '#e53935' }} />,
  'image/jpeg': <ImageIcon fontSize="small" sx={{ color: '#43a047' }} />,
  'image/png': <ImageIcon fontSize="small" sx={{ color: '#43a047' }} />,
  'image/jpg': <ImageIcon fontSize="small" sx={{ color: '#43a047' }} />,
};

export default function DocumentsTab({ lead }) {
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [documentType, setDocumentType] = useState('other');
  const [description, setDescription] = useState('');
  const fileInputRef = useRef(null);

  const fetchDocuments = async () => {
    try {
      setLoading(true);
      const res = await salesAPI.getDocuments(lead._id);
      setDocuments(res.data.data || []);
    } catch {
      toast.error('Failed to load documents');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (lead?._id) fetchDocuments();
  }, [lead?._id]);

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const formData = new FormData();
    formData.append('document', file);
    formData.append('documentType', documentType);
    if (description.trim()) formData.append('description', description.trim());

    setUploading(true);
    try {
      await salesAPI.uploadDocument(lead._id, formData);
      toast.success('Document uploaded');
      setDescription('');
      setDocumentType('other');
      fetchDocuments();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Upload failed');
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleDelete = async (docId) => {
    if (!window.confirm('Delete this document?')) return;
    try {
      await salesAPI.deleteDocument(lead._id, docId);
      toast.success('Document deleted');
      setDocuments((prev) => prev.filter((d) => d._id !== docId));
    } catch {
      toast.error('Failed to delete document');
    }
  };

  const handleDownload = async (doc) => {
    try {
      const url = salesAPI.downloadDocument(lead._id, doc._id);
      const token = localStorage.getItem('token');
      const response = await fetch(url, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!response.ok) throw new Error('Download failed');
      const blob = await response.blob();
      const blobUrl = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = blobUrl;
      a.download = doc.fileName || 'document';
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(blobUrl);
    } catch {
      toast.error('Failed to download document');
    }
  };

  return (
    <Box>
      {/* ── Upload Section ──────────────────────────────────────────────── */}
      <Paper variant="outlined" sx={{ p: 2, mb: 2, borderRadius: 2 }}>
        <Typography variant="subtitle2" fontWeight={700} mb={1.5}>Upload Document</Typography>
        <Stack gap={1.5}>
          <Stack direction="row" gap={1}>
            <FormControl size="small" sx={{ minWidth: 180 }}>
              <InputLabel>Document Type</InputLabel>
              <Select
                value={documentType}
                label="Document Type"
                onChange={(e) => setDocumentType(e.target.value)}
              >
                {DOC_TYPE_OPTIONS.map((o) => (
                  <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              size="small"
              placeholder="Description (optional)"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              sx={{ flex: 1 }}
            />
          </Stack>
          <Box>
            <input
              ref={fileInputRef}
              type="file"
              accept=".pdf,.jpg,.jpeg,.png,.doc,.docx,.xls,.xlsx,.csv"
              style={{ display: 'none' }}
              onChange={handleUpload}
            />
            <Button
              variant="contained"
              startIcon={uploading ? <CircularProgress size={16} color="inherit" /> : <UploadIcon />}
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading}
              size="small"
            >
              {uploading ? 'Uploading…' : 'Choose File & Upload'}
            </Button>
          </Box>
        </Stack>
      </Paper>

      {/* ── Documents List ──────────────────────────────────────────────── */}
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1}>
        <Typography variant="subtitle2" fontWeight={700}>
          Documents ({documents.length})
        </Typography>
        <Tooltip title="Refresh">
          <IconButton size="small" onClick={fetchDocuments} disabled={loading}>
            <RefreshIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Stack>

      {loading && <LinearProgress sx={{ mb: 2 }} />}

      {!loading && documents.length === 0 && (
        <Typography color="text.secondary" textAlign="center" py={3}>
          No documents uploaded yet
        </Typography>
      )}

      <Stack gap={1}>
        {documents.map((doc) => (
          <Paper
            key={doc._id}
            elevation={0}
            sx={{
              p: 1.5,
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 2,
              '&:hover': { bgcolor: 'action.hover' },
            }}
          >
            <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
              <Stack direction="row" gap={1} alignItems="flex-start" sx={{ flex: 1, minWidth: 0 }}>
                <Box sx={{ mt: 0.25 }}>
                  {FILE_ICONS[doc.mimeType] || <DocIcon fontSize="small" sx={{ color: '#757575' }} />}
                </Box>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="body2" fontWeight={600} noWrap>
                    {doc.documentName || doc.fileName}
                  </Typography>
                  <Stack direction="row" gap={0.5} alignItems="center" flexWrap="wrap">
                    <Chip
                      label={DOC_TYPE_LABELS[doc.documentType] || doc.documentType}
                      size="small"
                      sx={{ height: 18, fontSize: '0.65rem' }}
                    />
                    <Chip
                      label={doc.status || 'pending'}
                      size="small"
                      color={STATUS_COLORS[doc.status] || 'default'}
                      sx={{ height: 18, fontSize: '0.65rem' }}
                    />
                    <Typography variant="caption" color="text.disabled">
                      {fmtSize(doc.fileSize)}
                    </Typography>
                  </Stack>
                  <Typography variant="caption" color="text.disabled" display="block">
                    {fmtDate(doc.createdAt)}
                    {doc.description ? ` · ${doc.description}` : ''}
                  </Typography>
                </Box>
              </Stack>
              <Stack direction="row" gap={0.25}>
                <Tooltip title="Download">
                  <IconButton size="small" onClick={() => handleDownload(doc)}>
                    <DownloadIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Delete">
                  <IconButton size="small" onClick={() => handleDelete(doc._id)}>
                    <DeleteIcon fontSize="small" sx={{ color: 'error.light' }} />
                  </IconButton>
                </Tooltip>
              </Stack>
            </Stack>
          </Paper>
        ))}
      </Stack>
    </Box>
  );
}
